import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Download, TrendingUp, TrendingDown, Wallet, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const KIND_LABEL = {
  order: "Order Placed", payment: "Payment", cert_fee: "Certificate Fee",
  refund: "Refund", credit_drawdown: "Credit Drawn", credit_repay: "Credit Repaid",
};
const KIND_TONE = {
  order: "text-[#D8B23E]", payment: "text-[#2FB999]", cert_fee: "text-[#B87333]",
  refund: "text-[#2FB999]", credit_drawdown: "text-[#C0392B]", credit_repay: "text-[#2FB999]",
};

export default function AccountStatement() {
  const { user, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0, balance: 0 });
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/statements/mine"),
        api.get("/credit/mine").catch(() => ({ data: null })),
      ]);
      setRows(s.data.rows || []); setTotals(s.data.totals || {});
      setCredit(c.data);
    } catch (e) {
      toast.error("Could not load statement");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/statements/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statement_${(user?.email || "vinci")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.kind === filter);

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1400px] mx-auto">
        <Link to="/account" data-testid="statement-back" className="inline-flex items-center gap-2 text-[#8A8A82] hover:text-[#B87333] text-xs uppercase tracking-[0.2em] font-grotesk mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-white/10 pb-6">
          <div>
            <div className="overline text-[#B87333] mb-2">Account Statement</div>
            <h1 className="font-serif text-4xl md:text-5xl">Your Ledger</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2">
              Chronological history of every order, payment, refund, and credit movement on your Vinci account.
            </p>
          </div>
          <div className="flex gap-3">
            <button data-testid="statement-refresh" onClick={load} className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#B87333] flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button data-testid="statement-export" onClick={downloadCsv} className="rounded-full bg-[#B87333] text-black px-5 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2 hover:brightness-110">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Totals cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Debits" value={inr(totals.debit)} icon={<TrendingUp className="w-4 h-4 text-[#D8B23E]" />} testid="stat-debits" />
          <StatCard label="Total Credits" value={inr(totals.credit)} icon={<TrendingDown className="w-4 h-4 text-[#2FB999]" />} testid="stat-credits" />
          <StatCard label="Net Position" value={inr(totals.balance)} icon={<Wallet className="w-4 h-4 text-[#B87333]" />} testid="stat-balance" />
          {credit?.active ? (
            <StatCard label="Credit Available"
              value={inr(credit.available)}
              icon={<Wallet className="w-4 h-4 text-[#2FB999]" />}
              subtitle={`Limit ${inr(credit.limit)} · Outstanding ${inr(credit.outstanding)}`}
              testid="stat-credit" />
          ) : (
            <StatCard label="Credit Line" value="—" subtitle="Not enabled" icon={<Wallet className="w-4 h-4 text-[#8A8A82]" />} testid="stat-credit" />
          )}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[["all", "All"], ["order", "Orders"], ["payment", "Payments"], ["cert_fee", "Certificates"], ["refund", "Refunds"], ["credit_drawdown", "Credit Used"], ["credit_repay", "Repayments"]].map(([k, label]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] font-grotesk transition-colors ${filter === k ? "bg-[#F5F5F0] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-2xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[160px_120px_180px_1fr_140px_140px] gap-4 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">
            <div>Date</div><div>Type</div><div>Reference</div><div>Description</div><div className="text-right">Debit</div><div className="text-right">Credit</div>
          </div>
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-[#8A8A82] font-body text-sm">No entries yet.</div>
          ) : (
            filtered.map((r, i) => (
              <div key={i} data-testid={`stmt-row-${i}`} className="grid grid-cols-1 md:grid-cols-[160px_120px_180px_1fr_140px_140px] gap-4 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <div className="text-xs text-[#C9C9C2]">{fmtDate(r.date)}</div>
                <div className={`text-xs font-grotesk uppercase tracking-[0.15em] ${KIND_TONE[r.kind] || "text-[#8A8A82]"}`}>{KIND_LABEL[r.kind] || r.kind}</div>
                <div className="text-xs text-[#F5F5F0] font-mono">{r.ref}</div>
                <div className="text-xs text-[#C9C9C2]">{r.description}</div>
                <div className="text-xs text-right text-[#D8B23E]">{r.debit > 0 ? inr(r.debit) : ""}</div>
                <div className="text-xs text-right text-[#2FB999]">{r.credit > 0 ? inr(r.credit) : ""}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtitle, icon, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-3">
        {icon}<span>{label}</span>
      </div>
      <div className="font-serif text-3xl">{value}</div>
      {subtitle ? <div className="text-[10px] text-[#8A8A82] mt-2 font-body">{subtitle}</div> : null}
    </div>
  );
}
